import { useState, useRef, useEffect, useCallback } from 'react';
import { highlightMatch } from '../hooks/useSearch';
import { describeRecurrence } from '../utils/recurrence';

const PRI_COLORS = { high: '#DC2626', medium: '#D97706', low: '#16A34A' };

function SearchIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

function RepeatIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/>
      <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/>
    </svg>
  );
}

// ── Pulsing skeleton row ──────────────────────────────────────────────────
function SkeletonRow({ width = '60%' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--divider)' }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--border)', flexShrink: 0, animation: 'searchPulse 1.4s ease-in-out infinite' }} />
      <div style={{ flex: 1 }}>
        <div style={{ height: 12, borderRadius: 4, background: 'var(--border)', width, animation: 'searchPulse 1.4s ease-in-out infinite', marginBottom: 5 }} />
        <div style={{ height: 9, borderRadius: 4, background: 'var(--border)', width: '35%', animation: 'searchPulse 1.4s ease-in-out 0.2s infinite' }} />
      </div>
    </div>
  );
}

// ── Single result row ─────────────────────────────────────────────────────
function SearchResultRow({ task, query, onClick, isActive, onMouseEnter }) {
  const titleParts = highlightMatch(task.title, query);
  const priColor   = PRI_COLORS[task.priority] || PRI_COLORS.medium;
  const isRecurring = task.recurrence_type && task.recurrence_type !== 'none';

  let recurLabel = null;
  if (isRecurring) {
    try {
      const cfg = typeof task.recurrence_config === 'string'
        ? JSON.parse(task.recurrence_config) : task.recurrence_config;
      recurLabel = describeRecurrence(task.recurrence_type, cfg);
    } catch {}
  }

  return (
    <div
      onClick={() => onClick(task)}
      onMouseEnter={onMouseEnter}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', cursor: 'pointer',
        borderBottom: '1px solid var(--divider)',
        background: isActive ? 'var(--hover)' : 'transparent',
        transition: 'background 0.1s',
      }}
    >
      <div style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: task.done ? 'var(--text3)' : priColor, opacity: task.done ? 0.4 : 1 }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, lineHeight: 1.4, color: task.done ? 'var(--text3)' : 'var(--text)', textDecoration: task.done ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {titleParts.map((part, i) => (
            <span key={i} style={part.highlight ? {
              background: 'var(--accent-light)', color: 'var(--accent)',
              borderRadius: 3, padding: '0 1px', fontWeight: 600,
            } : {}}>{part.text}</span>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {task.category && (
            <span style={{ fontSize: 10, fontWeight: 500, color: task.category.color || 'var(--text3)' }}>
              {task.category.icon && task.category.icon + ' '}{task.category.name}
            </span>
          )}
          {task.due_date && (
            <span style={{ fontSize: 10, color: 'var(--text3)' }}>
              {new Date(task.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
          {isRecurring && recurLabel && (
            <span style={{ fontSize: 10, color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <RepeatIcon /> {recurLabel}
            </span>
          )}
        </div>
      </div>

      {task.done && (
        <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'var(--border)', color: 'var(--text3)', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Done</span>
      )}
    </div>
  );
}

// ── Main SearchBar ────────────────────────────────────────────────────────
export default function SearchBar({ query, onChange, onClear, results, isSearching, isActive, onSelectResult }) {
  const inputRef    = useRef(null);
  const [cursor, setCursor] = useState(-1);

  // Reset cursor when results change
  useEffect(() => { setCursor(-1); }, [results]);

  // ⌘K / Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter' && cursor >= 0) { e.preventDefault(); onSelectResult(results[cursor]); }
    else if (e.key === 'Escape') { onClear(); inputRef.current?.blur(); }
  };

  const showDropdown = isActive && (results.length > 0 || isSearching || (query.trim().length >= 2 && !isSearching));

  return (
    <div style={{ position: 'relative', marginBottom: 16 }}>
      <style>{`
        @keyframes searchPulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }
        @keyframes searchSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Input */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 9,
        background: 'var(--surface)',
        border: `1.5px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: showDropdown ? '10px 10px 0 0' : 10,
        padding: '9px 12px',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxShadow: isActive ? '0 0 0 3px var(--accent-ring)' : 'none',
      }}>
        <SearchIcon color={isActive ? 'var(--accent)' : 'var(--text3)'} />

        <input
          ref={inputRef}
          value={query}
          onChange={e => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search tasks…"
          style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            fontSize: 13, color: 'var(--text)', fontFamily: 'inherit',
          }}
        />

        {/* Right side: spinning loader OR clear button OR kbd hint */}
        {isSearching ? (
          <div style={{ width: 16, height: 16, border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'searchSpin 0.65s linear infinite', flexShrink: 0 }} />
        ) : isActive ? (
          <button onClick={() => { onClear(); inputRef.current?.focus(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', display: 'flex', padding: 2, borderRadius: 4, transition: 'color 0.1s' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
          >
            <XIcon />
          </button>
        ) : (
          <kbd style={{ fontSize: 10, padding: '2px 6px', background: 'var(--border)', borderRadius: 5, color: 'var(--text3)', fontFamily: 'inherit', lineHeight: '16px' }}>⌘K</kbd>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: 'var(--surface)',
          border: '1.5px solid var(--accent)', borderTop: 'none',
          borderRadius: '0 0 10px 10px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          overflow: 'hidden',
          maxHeight: 360, overflowY: 'auto',
        }}>

          {/* Loading: skeleton rows */}
          {isSearching && results.length === 0 && (
            <>
              <SkeletonRow width="70%" />
              <SkeletonRow width="50%" />
              <SkeletonRow width="60%" />
            </>
          )}

          {/* Results */}
          {!isSearching && results.length > 0 && (
            <>
              {/* Result count header */}
              <div style={{ padding: '6px 14px 5px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--divider)' }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {results.length} result{results.length !== 1 ? 's' : ''}
                </span>
                <span style={{ fontSize: 10, color: 'var(--text3)' }}>↑↓ navigate · ↵ open</span>
              </div>

              {results.slice(0, 12).map((task, i) => (
                <SearchResultRow
                  key={task.id}
                  task={task}
                  query={query}
                  onClick={onSelectResult}
                  isActive={cursor === i}
                  onMouseEnter={() => setCursor(i)}
                />
              ))}

              {results.length > 12 && (
                <div style={{ padding: '8px 14px', fontSize: 11, color: 'var(--text3)', textAlign: 'center', borderTop: '1px solid var(--divider)' }}>
                  {results.length - 12} more — refine your search
                </div>
              )}
            </>
          )}

          {/* Empty state */}
          {!isSearching && results.length === 0 && query.trim().length >= 2 && (
            <div style={{ padding: '28px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🔍</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                No results for "{query}"
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                Try different words or check completed tasks
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}